import { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { TeamGroup } from '@tassomutuo-backoffice/types';

interface IProps {
  isPending: boolean;
  teamGroups: TeamGroup[];
  onDeleteIconClick: (row: TeamGroup) => void;
  onManageGroupLeaders: (row: TeamGroup) => void;
  onViewGroupDetails: (row: TeamGroup) => void;
  disabled: boolean;
}

const TeamsDetailGroupTable = ({
  isPending,
  teamGroups,
  onDeleteIconClick,
  onManageGroupLeaders,
  onViewGroupDetails,
  disabled,
}: IProps) => {
  const [anchorElActionsMenu, setAnchorElActionsMenu] =
    useState<null | HTMLElement>(null);
  const [selectedGroup, setSelectedGroup] = useState<TeamGroup | null>(null);
  const actionsMenuOpen = Boolean(anchorElActionsMenu);

  const handleActionMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    group: TeamGroup
  ) => {
    setAnchorElActionsMenu(event.currentTarget);
    setSelectedGroup(group);
  };

  const handleCloseActionsMenu = () => {
    setAnchorElActionsMenu(null);
    setSelectedGroup(null);
  };

  const handleManageGroupLeaders = () => {
    if (selectedGroup) {
      onManageGroupLeaders(selectedGroup);
      handleCloseActionsMenu();
    }
  };

  const handleViewDetails = () => {
    if (selectedGroup) {
      onViewGroupDetails(selectedGroup);
      handleCloseActionsMenu();
    }
  };

  const tableColumns: GridColDef<TeamGroup>[] = [
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1,
      maxWidth: 300,
    },
    {
      field: 'count',
      headerName: 'Membri',
      flex: 1,
      maxWidth: 100,
      renderCell: ({ row }) => {
        const count = row.members?.length || 0;
        return <Typography>{count}</Typography>;
      },
    },
    {
      field: 'leaders',
      headerName: 'Capi gruppo',
      flex: 1,
      renderCell: ({ row }) => {
        if (!row.leaders?.length) return <Typography></Typography>;

        const firstLeader = row.leaders[0].email;
        const additionalCount = row.leaders.length - 1;

        return (
          <Typography>
            {firstLeader}
            {additionalCount > 0 && ` +${additionalCount}`}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 150,
      renderCell: ({ row }) => {
        const count = row.members?.length || 0;
        return (
          <Box display="flex" gap={1}>
            <IconButton
              disabled={count > 0 || disabled}
              onClick={() => onDeleteIconClick(row)}
            >
              <DeleteIcon />
            </IconButton>
            <IconButton onClick={(event) => handleActionMenuClick(event, row)}>
              <MoreVertIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <div>
      <DataGrid
        columns={tableColumns}
        rows={teamGroups || []}
        disableRowSelectionOnClick
        autoHeight
        loading={isPending}
        pagination
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
        pageSizeOptions={[10]}
      />
      <Menu
        anchorEl={anchorElActionsMenu}
        open={actionsMenuOpen}
        onClose={handleCloseActionsMenu}
        elevation={1}
      >
        <MenuItem onClick={handleViewDetails}>Visualizza dettagli</MenuItem>

        <MenuItem onClick={handleManageGroupLeaders}>
          Gestisci capi gruppo
        </MenuItem>
      </Menu>
    </div>
  );
};

export default TeamsDetailGroupTable;
